import { motion } from "framer-motion";
import { ArrowRight, ArrowDown, ArrowUpRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import HeroCarousel from "@/components/fittin/HeroCarousel";
import FitKeyPass, { WalletBadges } from "@/components/fittin/FitKeyPass";

// Hero visual for shoppers: the FitKey Wallet pass is the centerpiece.
function HeroPass({ onCreate }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.1 }}
      data-testid="hero-fitkey"
      className="relative w-full max-w-[360px] mx-auto lg:ml-auto lg:mr-4"
    >
      <div className="relative">
        {/* layered depth: two passes tucked behind */}
        <div
          aria-hidden
          className="absolute inset-0 translate-x-6 translate-y-3 rotate-[7deg] rounded-[28px] glass-card"
        />
        <div
          aria-hidden
          className="absolute inset-0 translate-x-3 translate-y-1.5 rotate-[3.5deg] rounded-[28px] bg-neutral-800 ring-1 ring-inset ring-white/10"
        />
        <motion.div
          animate={{ y: [0, -6, 0] }}
          transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
          className="relative"
        >
          <FitKeyPass
            size={{ unit: "EU", value: "42" }}
            brand="Adidas"
            model="NMD R1"
            fitkeyId="fk_sample_7c2f"
            sample
            testid="hero-fitkey-pass"
          />
        </motion.div>
      </div>
      <WalletBadges
        mode="decorative"
        onClick={onCreate}
        testid="hero-wallet-badges"
        className="mt-8 flex justify-center"
      />
    </motion.div>
  );
}

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1], delay: i * 0.08 },
  }),
};

export default function Hero({ variant = "shopper" }) {
  const isShopper = variant === "shopper";
  const navigate = useNavigate();

  const scrollTo = (id) => {
    const el = document.querySelector(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const copy = isShopper
    ? {
        badge: "No account. It lives in your Wallet.",
        title: "Your true size,",
        accent: "in every brand.",
        sub:
          "Tell us the one shoe that fits you best. Fittin turns it into a FitKey — a pass in your Apple or Google Wallet that knows your size in every brand, and lets you confirm the fit or return a pair in one tap.",
        primary: { label: "Create your FitKey", action: () => scrollTo("#create-fitkey"), testid: "hero-cta-fitkey" },
        secondary: { label: "For companies", action: () => navigate("/companies"), testid: "hero-cta-companies" },
        footnote: "Free · No account, no password",
        trailing: "Apple & Google Wallet",
      }
    : {
        badge: "Brand-accurate fit. Fewer returns.",
        title: "Fewer returns through",
        accent: "brand-accurate fit.",
        sub:
          "Your shoppers bring the fit data as a portable FitID. You map it to your catalog and sales data. Fewer returns, higher conversion, no guesswork per brand.",
        primary: { label: "Book a pilot", action: () => scrollTo("#contact"), testid: "hero-cta-pilot" },
        secondary: { label: "See integration", action: () => scrollTo("#integration"), testid: "hero-cta-integration" },
        footnote: "30-day paid pilot · Onboarding in under a week",
        trailing: "SOC 2 · GDPR",
      };

  return (
    <section
      id="top"
      data-testid={`hero-${variant}`}
      className="relative grain pt-32 md:pt-40 pb-20 md:pb-28"
    >
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.35]"
        style={{
          backgroundImage:
            "linear-gradient(to right, rgba(0,0,0,0.04) 1px, transparent 1px), linear-gradient(to bottom, rgba(0,0,0,0.04) 1px, transparent 1px)",
          backgroundSize: "56px 56px",
          maskImage: "radial-gradient(ellipse at 50% 0%, black 30%, transparent 75%)",
          WebkitMaskImage: "radial-gradient(ellipse at 50% 0%, black 30%, transparent 75%)",
        }}
      />

      <div className="relative mx-auto max-w-7xl px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
        <div className="lg:col-span-7">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            animate="show"
            custom={0}
            data-testid="hero-badge"
            className="inline-flex items-center gap-2 rounded-full border border-black/10 bg-white/60 backdrop-blur px-3.5 py-1.5 text-[12px] text-neutral-700"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
            {copy.badge}
          </motion.div>

          <motion.h1
            variants={fadeUp}
            initial="hidden"
            animate="show"
            custom={1}
            data-testid="hero-headline"
            className="mt-6 text-[56px] leading-[0.95] sm:text-7xl lg:text-[96px] font-medium tracking-[-0.04em]"
          >
            {copy.title}{" "}
            <span className="italic font-light text-neutral-500">{copy.accent}</span>
          </motion.h1>

          <motion.p
            variants={fadeUp}
            initial="hidden"
            animate="show"
            custom={2}
            data-testid="hero-subheadline"
            className="mt-6 max-w-xl text-[17px] md:text-lg text-neutral-600 leading-relaxed"
          >
            {copy.sub}
          </motion.p>

          <motion.div
            variants={fadeUp}
            initial="hidden"
            animate="show"
            custom={3}
            className="mt-9 flex flex-col sm:flex-row items-start sm:items-center gap-3"
          >
            <button
              data-testid={copy.primary.testid}
              onClick={copy.primary.action}
              className="group inline-flex items-center gap-2 rounded-full bg-[#0A0A0A] text-white px-5 py-3 text-[14px] hover:bg-neutral-800 transition-all active:scale-[0.98]"
            >
              {copy.primary.label}
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </button>
            <button
              data-testid={copy.secondary.testid}
              onClick={copy.secondary.action}
              className="group inline-flex items-center gap-2 rounded-full bg-white border border-black/10 px-5 py-3 text-[14px] hover:bg-neutral-50 transition-all"
            >
              {copy.secondary.label}
              {isShopper ? (
                <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              ) : (
                <ArrowDown className="h-4 w-4 transition-transform group-hover:translate-y-0.5" />
              )}
            </button>
          </motion.div>

          <motion.div
            variants={fadeUp}
            initial="hidden"
            animate="show"
            custom={4}
            className="mt-14 flex items-center gap-6 text-[11px] uppercase tracking-[0.18em] text-neutral-500"
          >
            <span>{copy.footnote}</span>
            <span className="flex-1 h-px bg-black/10" />
            <span>{copy.trailing}</span>
          </motion.div>
        </div>

        <div className="lg:col-span-5">
          {isShopper ? <HeroPass onCreate={() => scrollTo("#create-fitkey")} /> : <HeroCarousel />}
        </div>
      </div>
    </section>
  );
}
